import { useEffect, useState } from 'react'
import { Box, Chip, Tab, Tabs, Typography } from '@mui/material'
import FiberManualRecordRoundedIcon from '@mui/icons-material/FiberManualRecordRounded'
import CardWithSpace from '../../../components/Cards/CardWithSpace/index.jsx'
import TitleWithIcon from '../../../components/Cards/helpers/TitleWithIcon.jsx'
import UseTranslation from '../../../hooks/UseTranslation.jsx'
import { ReactComponent as DocumentLinedIcon } from '../../../images/DocumentLined.svg'
import { decodeHtmlString } from '../../../utils/helpers'

const ConsentForm = ({ companyPolicies }) => {
  const { t, language } = UseTranslation()
  const normalizedLanguage = String(language || '').trim().toUpperCase()
  const isArabicSiteLanguage = normalizedLanguage === 'AR'
  const hasEnglishMessage = Boolean(companyPolicies?.message_en)
  const hasArabicMessage = Boolean(companyPolicies?.message_ar)
  const hasPolicyMessage = hasEnglishMessage || hasArabicMessage
  const getDefaultLang = () => {
    if (isArabicSiteLanguage && hasArabicMessage) return 'ar'
    if (!isArabicSiteLanguage && hasEnglishMessage) return 'en'
    if (hasEnglishMessage) return 'en'
    if (hasArabicMessage) return 'ar'
    return isArabicSiteLanguage ? 'ar' : 'en'
  }
  const [lang, setLang] = useState(getDefaultLang)
  const isPublished = Boolean(companyPolicies?.enabled)
  const policyMessage = lang === 'ar'
    ? companyPolicies?.message_ar
    : companyPolicies?.message_en

  useEffect(() => {
    setLang(getDefaultLang())
  }, [isArabicSiteLanguage, companyPolicies?.message_ar, companyPolicies?.message_en])

  const tabOptions = [
    { value: 'en', label: t?.English },
    { value: 'ar', label: t?.['العربية'] },
  ]

  if (!hasPolicyMessage) return null

  return (
    <Box sx={styles.sectionGroup}>
      <Box sx={styles.sectionHeader}>
        <TitleWithIcon
          title={t?.['Consent Form']}
          icon={<DocumentLinedIcon />}
          iconSize="sm"
          customStyle={styles.sectionOverline}
        />
      </Box>

      <CardWithSpace customStyles={styles.cardShell}>
        <Box sx={styles.cardHeader}>
          <Box sx={styles.headerTitles}>
            <Typography component="h2" sx={styles.cardTitle}>
              {t?.['Consent Form']}
            </Typography>
            <Typography sx={styles.cardSubtitle}>
              {t?.['Consent for below Data - every employee must read and approve before using the system']}
            </Typography>
          </Box>
          <Chip
            size="small"
            icon={(
              <FiberManualRecordRoundedIcon
                sx={styles.statusDotIcon(isPublished ? '#29CC8B' : '#92929d')}
              />
            )}
            label={isPublished ? t?.Published : t?.Disabled}
            sx={isPublished ? styles.publishedChip : styles.disabledChip}
          />
        </Box>

        <Box sx={styles.tabsWrap}>
          <Tabs
            value={lang}
            onChange={(_, v) => setLang(v)}
            sx={[
              styles.tabs,
              isArabicSiteLanguage && styles.tabsReversed,
            ]}
          >
            {tabOptions.map((option) => (
              <Tab key={option.value} value={option.value} label={option.label} sx={styles.tab} />
            ))}
          </Tabs>
        </Box>

        <Box sx={styles.contentWrap}>
          <Typography
            component="div"
            dir={lang === 'ar' ? 'rtl' : 'ltr'}
            sx={[
              styles.contentText,
              lang === 'ar' ? styles.contentTextAr : styles.contentTextEn,
            ]}
          >
            {decodeHtmlString(policyMessage)}
          </Typography>
        </Box>
      </CardWithSpace>
    </Box>
  )
}

const styles = {
  statusDotIcon: (color) => ({
    '&&': {
      color: `${color} !important`,
      fontSize: 14,
    },
  }),
  sectionGroup: {
    width: '100%',
    mb: { xs: 2.5, sm: 3 },
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: 2,
    mb: 1.25,
  },
  sectionOverline: {
    fontSize: '0.6875rem',
    fontWeight: 600,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    color: '#92929d',
    lineHeight: 1.3,
    '& svg': {
      color: '#92929d',
    },
  },
  cardShell: {
    border: '1px solid #e8eaed',
    borderRadius: '12px',
    boxShadow: 'none',
    marginBottom: 0,
    overflow: 'hidden',
    padding: 0,
  },
  cardHeader: {
    px: { xs: 2.5, sm: 3 },
    py: { xs: 2.5, sm: 2.5 },
    borderBottom: '1px solid #ebecef',
    display: 'flex',
    alignItems: { xs: 'flex-start', sm: 'center' },
    justifyContent: 'space-between',
    gap: 1.5,
  },
  headerTitles: {
    minWidth: 0,
    flex: 1,
  },
  cardTitle: {
    fontWeight: 700,
    fontSize: { xs: '1rem', sm: '1.0625rem' },
    color: '#1b2559',
    lineHeight: 1.35,
    letterSpacing: '-0.01em',
  },
  cardSubtitle: {
    mt: 0.75,
    fontSize: { xs: '0.75rem', sm: '0.8125rem' },
    fontWeight: 400,
    color: '#92929d',
    lineHeight: 1.45,
  },
  publishedChip: {
    height: 26,
    flexShrink: 0,
    fontWeight: 600,
    fontSize: '0.75rem',
    color: '#19935f',
    backgroundColor: 'rgba(41, 204, 139, 0.12)',
    border: '1px solid #29CC8B',
    '& .MuiChip-icon': {
      marginLeft: '8px',
    },
  },
  disabledChip: {
    height: 26,
    flexShrink: 0,
    fontWeight: 600,
    fontSize: '0.75rem',
    color: '#696974',
    backgroundColor: 'rgba(146, 146, 157, 0.12)',
    border: '1px solid rgba(146, 146, 157, 0.32)',
    '& .MuiChip-icon': {
      marginLeft: '8px',
    },
  },
  tabsWrap: {
    backgroundColor: '#F8F9FA',
    borderBottom: '1px solid #ebecef',
    px: { xs: 2.5, sm: 3 },
    pt: 0,
  },
  tabs: {
    minHeight: 55,
    '& .MuiTabs-indicator': {
      backgroundColor: '#29CC8B',
      height: 2,
    },
  },
  tabsReversed: {
    '& .MuiTabs-flexContainer': {
      flexDirection: 'row-reverse',
      justifyContent: 'flex-end',
    },
  },
  tab: {
    textTransform: 'none',
    minHeight: 48,
    px: 2.25,
    fontWeight: 600,
    fontSize: { xs: '0.875rem', sm: '0.9375rem' },
    color: '#92929d',
    '&.Mui-selected': {
      color: '#19935f',
      backgroundColor: '#ffffff',
    },
  },
  contentWrap: {
    px: { xs: 2.5, sm: 3 },
    py: { xs: 2.5, sm: 3 },
  },
  contentText: {
    margin: 0,
    whiteSpace: 'pre-line',
    wordBreak: 'break-word',
    unicodeBidi: 'plaintext',
    fontSize: { xs: '0.95rem', sm: '1.0625rem' },
    lineHeight: 1.9,
    color: '#101828',
  },
  contentTextAr: {
    direction: 'rtl',
    textAlign: 'right',
  },
  contentTextEn: {
    direction: 'ltr',
    textAlign: 'left',
  },
}

export default ConsentForm
